import store from '../store_instance';
import { shade } from 'lib/util';
import { pcui } from 'lib/perspective-creation-ui';
import { nui } from 'lib/nodes-ui';
import { KEY_CODES } from 'lib/constants';

class PerspectivesUI {

  constructor() {
    this.persistedPerspective = null;
    this.activePerspectives = {};
    this.persistedPerspectives = {};
    this.activeTextFields = {};
    this.style = document.createElement('style');
    document.head.appendChild(this.style);
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    this.operation = canvas.operation;
  }
  setActiveAnnotation(perspectiveId,nodeId,bool) {
    if (!this.activeTextFields[perspectiveId]) {
      this.activeTextFields[perspectiveId] = {};
    }
    this.activeTextFields[perspectiveId][nodeId] = bool;
  }
  isActiveAnnotation(perspectiveId,nodeId) {
    if (!this.activeTextFields[perspectiveId]) {
      return false;
    }
    return this.activeTextFields[perspectiveId][nodeId];
  }

  perspectiveIsActive(id) {
    if (this.activePerspectives[id]) {
      return true;
    }
    return false;
  }

  perspectiveIsPersistent(id) {
    if (this.persistedPerspective && this.persistedPerspective.id === id) {
      return true;
    }
    if (this.persistedPerspectives[id]) {
      return true;
    }
    return false;
  }

  addActivePerspective(perspective) {
    this.activePerspectives[perspective.id] = perspective;
  }
  removeActivePerspective(perspective) {
    delete this.activePerspectives[perspective.id];
  }

  addPersistentPerspective(perspective) {
    this.persistedPerspectives[perspective.id] = perspective;
  }
  removePersistentPerspective(perspective) {
    delete this.persistedPerspectives[perspective.id];
  }
  hasPersistentPerspective() {
    if (this.persistedPerspective) {
      return true;
    }
    return Boolean(Object.keys(this.persistedPerspectives).length);
  }

  addPerspectivePoint({ label, color, pointNodeUUID, operationMessages, members }) {
    const perspectiveUUID = store.addPerspective({ color, members, label, pointNodeUUID });
    const perspective = store.getPerspective(perspectiveUUID);

    const _operationMessages = [];

    _operationMessages.push(this.operation.messages.addPerspectivePoint({
      perspectiveUUID,
      label,
      color,
      members,
      orderIndex: perspective.orderIndex,
      pointMapNodeUUID: perspective.pointNodeUUID,
      onFailure: (err) => {
        console.log(`addPerspectivePoint operation failure: ${err}`);
      }
    }));

    if (pointNodeUUID && this.canvas.nodeTags[pointNodeUUID]) {
      this.canvas.nodeTags[pointNodeUUID].updatePerspectiveDots();
      this.canvas.nodeTags[pointNodeUUID].freshenEyeball();
    }

    if (operationMessages !== undefined) {
      for (const msg of _operationMessages) {
        operationMessages.push(msg);
      }
    } else {
      this.operation.batchOperation({
        messages: _operationMessages,
        onFailure: error => {
          console.log(`addPerspectivePoint batchOperation failed, ${error}`);
        }
      });
    }

    // if (store.getPerspectives().length === 1) {
    //   this.canvas.toolsSidebarTag.activateTab({ tabTagName: 'perspectives-listing' });
    // }
    return perspective;
  }

  addPerspectivePointNodeToMap({ perspectiveUUID, clientX, clientY }) {
    const perspective = store.getPerspective(perspectiveUUID);
    const { scale, tx, ty } = this.canvas.getMatrix();
    const nodeId = store.addNode({
      x: (clientX - tx - (1 * scale)) / scale,
      y: (clientY - ty - (24 * scale)) / scale,
      title: perspective.label,
      summary: "",
      createdById: window.user_id, // TODO fix
      orderIndex: 0,
      isNewNode: true,
    });
    const operationMessages = [];

    perspective.updatePointNode(nodeId);

    operationMessages.push(
      this.operation.messages.addNode({
        node: store.getNode(nodeId),
        onFailure: err => { console.log(`addNode failure: ${err}`); },
      }
      ));

    this.canvas.update();

    this.editPerspectivePointNode({
      perspectiveUUID: perspectiveUUID,
      pointNodeUUID: nodeId,
      operationMessages
    });
  }

  // pretend we're deleting it but allow it to be summoned back with the same content & style when clicking the eye tool
  hidePerspective(id) {
    const perspective = store.getPerspective(id);
    perspective.setHidden(true);
    if (perspective.pointNodeUUID && this.canvas.nodeTags[perspective.pointNodeUUID]) {
      this.canvas.nodeTags[perspective.pointNodeUUID].update();
      this.canvas.nodeTags[perspective.pointNodeUUID].updatePerspectiveDots();
    }
    this.refreshPerspectiveLists();
    this.updatePerspectiveNodeTags(perspective.id);
  }

  unhidePerspective(id) {
    const perspective = store.getPerspective(id);
    perspective.setHidden(false);
    if (perspective.pointNodeUUID && this.canvas.nodeTags[perspective.pointNodeUUID]) {
      this.canvas.nodeTags[perspective.pointNodeUUID].update();
      this.canvas.nodeTags[perspective.pointNodeUUID].updatePerspectiveDots();
    }
    this.refreshPerspectiveLists();
    this.updatePerspectiveNodeTags(perspective.id);
  }

  getPerspectiveListingItemTags() {
    let itemTags = this.canvas.perspectivesListingTag.tags['perspectives-listing-item'];
    if (!Array.isArray(itemTags)) itemTags = [itemTags];
    return itemTags;
  }

  setNodeAnnotationPosition(perspectiveId, nodeId, x, y, gravity) {
    const perspective = store.getPerspective(perspectiveId);
    const prevPosition = perspective.getNodeAnnotationPosition(nodeId);
    perspective.setNodeAnnotationPosition({nodeId, x, y, gravity});
    const position = {x, y};

    this.operation.updatePerspectiveNodeViews({
      nodeViews: [{
        perspectiveUUID: perspective.id,
        mapNodeUUID: nodeId,
        displayProperties: {annotationPosition: position}
      }],
      prevNodeViews: [{
        perspectiveUUID: perspective.id,
        mapNodeUUID: nodeId,
        displayProperties: {annotationPosition: prevPosition}
      }],
      onFailure: e => console.log(`updatePerspectiveNodeViews failure: ${e}`),
      onSuccess: () => this.updatePerspectiveNodeTags(perspectiveId),
    });
  }

  // when deleting a list of nodes, pass the result of store.deleteNodes here to clear out all linked perspective data
  deleteElements(deleteResult,operationMessages) {
    if (!deleteResult) {
      return;
    }
    for (const nodeId of deleteResult.deletedNodeIds) {
      const perspective = pui.getPerspectiveFromPointNode(nodeId);
      // if a point node was deleted, reset the view if it was active
      if (perspective) {
        pui.deletePerspective(perspective.id,operationMessages);
      }
    }

    const removeFromPerspectiveMessages = pui.removeFromAllPerspectives({
      nodeIds: deleteResult.deletedNodeIds,
      linkIds: deleteResult.deletedLinkIds
    });
    if (removeFromPerspectiveMessages) {
      operationMessages = operationMessages.concat(removeFromPerspectiveMessages);
    }
    this.refreshPerspectiveLists();
  }

  deletePerspective(id,operationMessages) {
    const message = this._deletePerspectiveOperation(id);
    if (operationMessages) {
      operationMessages.push(message);
      return;
    }
    this.operation.batchOperation({
      messages: [message],
      onFailure: err => {
        console.log(`deletePerspectivePoint operation failure: ${err}`);
      },
    });
  }
  // delete the perspective and return the operation message
  _deletePerspectiveOperation(id) {
    const perspective = store.getPerspective(id);
    if (!perspective) {
      return;
    }
    const perspectiveNodeTags = this.getPerspectiveNodeTags(perspective);

    if (pui.perspectiveIsActive(perspective.id)) {
      this.unsetActivePerspective(perspective);
    }

    store.deletePerspective(id);

    if (perspective.pointNodeUUID && this.canvas.nodeTags[perspective.pointNodeUUID]) {
      this.canvas.nodeTags[perspective.pointNodeUUID].updatePerspectiveDots();
      this.canvas.nodeTags[perspective.pointNodeUUID].freshenEyeball();
    }

    for (const perspectiveNodeTag of perspectiveNodeTags) {
      if (perspectiveNodeTag) {
        perspectiveNodeTag.updatePerspectiveDots();
      }
    }

    nui.bulkRealignLinks(perspectiveNodeTags);

    return this.operation.messages.deletePerspectivePoint({
      perspectiveUUID: id
    });
  }

  undeletePerspective(id,operationMessages) {
    const message = this._undeletePerspectiveOperation(id);

    if (operationMessages) {
      operationMessages.push(message);
      return;
    }

    this.operation.batchOperation({
      messages: [message],
      onFailure: err => {
        console.log(`deletePerspectivePoint operation failure: ${err}`);
      },
    });
  }

  _undeletePerspectiveOperation(id) {
    store.undeletePerspective(id);

    const perspective = store.getPerspective(id);
    if (!perspective) {
      return;
    }

    this.updatePerspectiveMembers([perspective]);

    return this.operation.messages.undeletePerspectivePoint({
      perspectiveUUID: id
    });
  }


  updatePerspectiveNodeTags(perspectiveUUID) {
    const perspective = store.getPerspective(perspectiveUUID);
    if (perspective.pointNodeUUID && this.canvas.nodeTags[perspective.pointNodeUUID]) {
      this.canvas.nodeTags[perspective.pointNodeUUID].freshenEyeball();
    }
    const perspectiveNodeTags = this.getPerspectiveNodeTags(perspective);
    for (const perspectiveNodeTag of perspectiveNodeTags) {
      if (perspectiveNodeTag) {
        perspectiveNodeTag.update({ forceUpdate: true });
      }
    }
  }

  updatePerspectiveLinkTags(perspectiveUUID) {
    const perspective = store.getPerspective(perspectiveUUID);
    const perspectiveLinkTags = this.getPerspectiveLinkTags(perspective);
    for (const perspectiveLinkTag of perspectiveLinkTags) {
      perspectiveLinkTag.update();
    }
  }

  updatePerspectiveNodeTagsDots(perspectiveUUID) {
    const perspective = store.getPerspective(perspectiveUUID);
    if (!perspective) {
      return;
    }
    const perspectiveNodeTags = this.getPerspectiveNodeTags(perspective);
    for (const perspectiveNodeTag of perspectiveNodeTags) {
      if (perspectiveNodeTag) {
        perspectiveNodeTag.updatePerspectiveDots();
      }
    }
  }

  editPerspectivePointNode({ perspectiveUUID, pointNodeUUID, operationMessages }) {
    const perspective = store.getPerspective(perspectiveUUID);
    const prevPointNodeUUID = perspective.pointNodeUUID;
    perspective.updatePointNode(pointNodeUUID);
    this.updatePerspectiveNodeTags(perspectiveUUID);
    this.updatePerspectiveLinkTags(perspectiveUUID);
    if (pointNodeUUID && this.canvas.nodeTags[pointNodeUUID]) {
      this.canvas.nodeTags[pointNodeUUID].freshenEyeball();
    }
    if (prevPointNodeUUID && this.canvas.nodeTags[prevPointNodeUUID]) {
      this.canvas.nodeTags[prevPointNodeUUID].freshenEyeball();
    }
    const updatePerspectivePointMessage = this.operation.messages.updatePerspectivePoint({
      perspectiveUUID,
      updates: {
        pointMapNodeUUID: pointNodeUUID,
      },
      prevValues: {
        pointMapNodeUUID: prevPointNodeUUID,
      },
    });

    if (!operationMessages) {
      operationMessages = [];
    }
    operationMessages.push(updatePerspectivePointMessage);

    this.operation.batchOperation({
      messages: operationMessages,
      onFailure: error => {
        console.log(`editPerspectivePointNode batchOperation failed, ${error}`);
      }
    });

  }

  editPerspectivePoint({ perspective, pointNodeUUID, title }) {
    const prevTitle = perspective.getTitle();
    const prevPointNodeUUID = perspective.pointNodeUUID;

    if (prevTitle && title && prevTitle === title && prevPointNodeUUID && pointNodeUUID && prevPointNodeUUID === pointNodeUUID) {
      // don't send operation if no change
      return;
    }

    perspective.label = title;
    perspective.pointNodeUUID = pointNodeUUID;

    this.operation.updatePerspectivePoint({
      perspectiveUUID: perspective.id,
      updates: {
        label: title,
        pointMapNodeUUID: pointNodeUUID,
      },
      prevValues: {
        label: prevTitle === undefined ? null : prevTitle,
        pointMapNodeUUID: prevPointNodeUUID === undefined ? null : prevPointNodeUUID,
      },
      onFailure: e => console.log(`editPerspectivePoint failure: ${e}`),
    });
  }

  // given a point node, change the perspective title and return the operation message to execute
  editPointNodeTitleOperation({ perspective, title }) {
    if (!perspective) {
      return;
    }

    const prevLabel = perspective.label;
    perspective.updateLabel(title);

    if (prevLabel == title) {
      // don't send operation if no change
      return;
    }
    const operationMessages = [];

    const message = this.operation.messages.updatePerspectivePoint({
      perspectiveUUID: perspective.id,
      updates: {
        label: title
      },
      prevValues: {
        label: prevLabel,
      }
    });

    return message;
  }

  editPerspectiveTitle({ perspectiveUUID, title, meta }) {
    const perspective = store.getPerspective(perspectiveUUID);

    const message = this.editPointNodeTitleOperation({ perspective, title });
    if (!message) {
      return;
    }

    const operationMessages = [message];

    if (perspective.pointNodeUUID) {
      const pointNode = store.getNode(perspective.pointNodeUUID);
      const prevTitle = pointNode.getTitle();
      const prevTitleMeta = prevTitle.meta;

      pointNode.setTitle(title, meta);
      this.canvas.nodeTags[perspective.pointNodeUUID].updateTitleDisplay();

      operationMessages.push(
        this.operation.messages.editNode({
          updates: { title, titleMeta: meta },
          prevValues: { title: prevTitle, titleMeta: prevTitleMeta },
          nodeUUID: perspective.pointNodeUUID,
          onFailure: e => console.log(`editNode failure: ${e}`),
        })
      );
    }


    if (operationMessages.length) {
      this.operation.batchOperation({
        messages: operationMessages,
        onFailure: (err) => {
          console.log(`exitPerspectiveTitle batchOperation failure: ${err}`);
        },
      });
    }

    this.updatePerspectiveNodeTags(perspectiveUUID);
    this.updatePerspectiveLinkTags(perspectiveUUID);
  }

  editPerspectiveColor({ perspectiveUUID, color }) {
    const perspective = store.getPerspective(perspectiveUUID);
    const prevColor = perspective.color;
    perspective.updateColor(color);
    this.updatePerspectiveNodeTags(perspectiveUUID);
    this.updatePerspectiveLinkTags(perspectiveUUID);
    this.canvas.perspectiveBannerTag.update();
    this.refreshPerspectiveLists();
    this.operation.updatePerspectivePoint({
      perspectiveUUID,
      updates: {
        color,
      },
      prevValues: {
        color: prevColor,
      },
      onFailure: err => {
        console.log(`editPerspectiveColor operation failure: ${err}`);
      },
    });
  }

  setHoverState(perspective) {
    const itemTags = this.getPerspectiveListingItemTags();
    if (!itemTags || !itemTags.length) {
      return;
    }
    for (const t of itemTags) {
      if (t.id == perspective.id) {
        t.refs.main.classList.add('bg-gray1');
      } else {
        t.refs.main.classList.remove('bg-gray1');
      }
    }
  }

  unsetHoverState() {
    const itemTags = this.getPerspectiveListingItemTags();
    if (!itemTags || !itemTags.length) {
      return;
    }
    for (const t of itemTags) {
      if (t && t.refs) {
        t.refs.main.classList.remove('bg-gray1');
      }
    }
  }

  areAllPerspectivesSelected() {
    if (pui.getActivePerspectiveCount() == pui.getPerspectives().length) {
      return true;
    }
    return false;
  }

  toggleShowAllPerspectives() {
    if (this.areAllPerspectivesSelected()) {
      // hide all perspectives
      this.unsetAllPerspectives();
    } else {
      // show all perspectives
      this.setBatchActivePerspective(this.getPerspectives());
    }
  }

  setAnnotationLayout(option) {
    const prevOption = this.canvas.annotationStyle;
    this.canvas.annotationStyle = option;
    this.canvas.redrawAnnotations();

    if (this.canvas.isViewOnly()) return;

    this.canvas.operation.updateMapDisplayProperties({
      displayProperties: {
        annotationStyle: option
      },
      prevDisplayProperties: {
        annotationStyle: prevOption
      }
    });
  }

  setActivePerspective(perspective, isPersisted) {
    if (!perspective) {
      return;
    }
    if (this.getActivePerspectiveCount() == 1 && perspective && this.perspectiveIsPersistent(perspective.id)) {
      return this.unsetActivePerspective(this.getActivePerspectives()[0]);
    }
    if (!isPersisted && this.perspectiveIsActive(perspective.id)) {
      return;
    }

    return this._setActivePerspective(perspective,isPersisted);
  }

  _setActivePerspective(perspective,isPersisted) {
    if (perspective) {
      this.addActivePerspective(perspective);
      if (isPersisted) {
        this.addPersistentPerspective(perspective);
      }

      this.setPerspectiveAnnotationsActive(perspective, true);

      this.refreshListingItemActivation(perspective, isPersisted);
    }

    if (isPersisted) {
      while (this.style.sheet.cssRules.length) {
        this.style.sheet.deleteRule(0);
      }
    }
    this.unHighlightNodes();
    this.highlightNodes();
    this._refreshPerspectiveContent([perspective]);
  }

  unsetActivePerspective(perspective) {
    if (!perspective) {
      console.error('missing perspective in unsetActivePerspective');
    }

    this.removeActivePerspective(perspective);
    this.removePersistentPerspective(perspective);
    if (this.getActivePerspectiveCount() == 1) {
      document.body.removeAttribute(`data-active-perspective-id-${perspective.id}`);
    }

    if (this.perspectiveIsPersistent(perspective.id)) {
      delete this.activeTextFields[perspective.id];
    }
    this.unsetHoverState();
    this.unHighlightNodes();

    if (this.getPersistentPerspectiveCount()) {
      this.highlightNodes();
    }

    const pointNodeTag = this.getPointNodeTag(perspective.id);
    if (pointNodeTag) {
      pointNodeTag.updatePerspectiveDotStyle();
    }

    this.setPerspectiveAnnotationsActive(perspective, false);

    this.refreshListingItemActivation();
    this.updatePerspectiveMembers([perspective]);

    this.canvas.tags['perspectives-banner'].update();
    if (this.canvas.annotationStyle == 'in_card') {
      canvas.realignAllLinks();
    }
  }

  setMultipleActivePerspective(perspective,isPersisted) {
    return this._setActivePerspective(perspective,isPersisted);
  }

  setBatchActivePerspective(perspectives) {
    const nodeTags = {};
    for (const perspective of perspectives) {
      this.addActivePerspective(perspective);
      this.addPersistentPerspective(perspective);
      this.setPerspectiveAnnotationsActive(perspective, true);
    }
    this.refreshListingItemActivation();
    this._refreshPerspectiveContent(perspectives);
    this.unHighlightNodes();
    this.highlightNodes();
  }


  _refreshPerspectiveContent(perspectives) {
    this.updatePerspectiveMembers(perspectives);
    this.canvas.tags['perspectives-banner'].update();
    if (this.canvas.annotationStyle == 'in_card') {
      this.canvas.realignAllLinks();
    }
  }

  setPerspectiveAnnotationsActive(perspective, bool) {
    if (this.canvas.perspectiveAnnotationTags[perspective.id]) {
      for (const annotationTag of Object.values(this.canvas.perspectiveAnnotationTags[perspective.id])) {
        if (Boolean(annotationTag._getAnnotation())) {
          annotationTag.setActive(bool);
        }
      }
    }
  }

  unsetAllPerspectives() {
    for (const perspective of Object.values(this.activePerspectives)) {
      this.setPerspectiveAnnotationsActive(perspective, false);
    }

    this.activePerspectives = {};
    this.persistedPerspectives = {};
    this.refreshListingItemActivation();
    this._refreshPerspectiveContent(this.getPerspectives());
    this.unHighlightNodes();
  }

  // refresh view of nodes & links in perspective a view
  updatePerspectiveMembers(perspectives) {
    if (!perspectives) {
      return;
    }
    const nodeTags = {};
    for (const perspective of perspectives) {
      if (perspective.pointNodeUUID && this.canvas.nodeTags[perspective.pointNodeUUID]) {
        this.canvas.nodeTags[perspective.pointNodeUUID].updatePerspectiveDotStyle();
      }
      const perspectiveNodeTags = this.getPerspectiveNodeTags(perspective);
      perspectiveNodeTags.map(n => {nodeTags[n.id] = n;});
    }


    for (const id in nodeTags) {
      const perspectiveNodeTag = nodeTags[id];
      if (perspectiveNodeTag) {
        perspectiveNodeTag.updatePerspectiveDotStyle();
      }
    }
  }

  refreshListingItemActivation(perspective,_isPersisted) {
    const itemTags = this.getPerspectiveListingItemTags();
    if (!itemTags) {
      return;
    }
    for (const t of itemTags) {
      if (!t || !t.id) {
        continue;
      }
      let isPersisted = Boolean(this.perspectiveIsPersistent(t.id));
      if (perspective && perspective.id == t.id) {
        isPersisted = _isPersisted;
      }
      if (this.perspectiveIsActive(t.id) ||
        (perspective && perspective.id == t.id)) {
        t.setActive(isPersisted);
      } else {
        t.unsetActive(true);
      }
    }
  }

  getActivePerspectives() {
    return Object.values(this.activePerspectives);
  }

  getPersistentPerspectives() {
    return Object.values(this.persistedPerspectives);
  }

  getActivePerspectiveCount() {
    return Object.keys(this.activePerspectives).length;
  }

  getPersistentPerspectiveCount() {
    return Object.keys(this.persistedPerspectives).length;
  }


  getPointNodeTag(perspectiveUUID) {
    const perspective = store.getPerspective(perspectiveUUID);
    if (!perspective) {
      return;
    }
    const node = store.getNode(perspective.pointNodeUUID);
    if (node && perspective && perspective.pointNodeUUID) {
      return this.canvas.nodeTags[perspective.pointNodeUUID];
    } else {
      return null;
    }
  }

  getPerspectiveFromPointNode(pointNodeUUID) {
    const perspective = store.getAllPerspectives().find(p => p.pointNodeUUID == pointNodeUUID);
    if (perspective) {
      return perspective;
    } else {
      return null;
    }
  }

  getDeletedPerspectiveFromPointNode(pointNodeUUID) {
    const perspective = store.deleted.perspectives.find(p => p.pointNodeUUID == pointNodeUUID);
    if (perspective) {
      return perspective;
    } else {
      return null;
    }
  }

  getActivePerspectives() {
    return Object.values(this.activePerspectives);
  }

  getPerspectivesPerElement() {
    const nodePerspectives = {};
    const linkPerspectives = {};
    const activePerspectives = this.getActivePerspectives();
    for (const perspective of activePerspectives) {
      const perspectiveId = perspective.id;
      const nodeTags = this.getPerspectiveNodeTags(perspective);
      const linkTags = this.getPerspectiveLinkTags(perspective);

      const pointNodeTag = this.getPointNodeTag(perspectiveId);
      if (pointNodeTag) {
        nodeTags.push(pointNodeTag);
      }
      for (const nt of nodeTags) {
        // if we've seen this node, remove it and ban it
        if (!nodePerspectives[nt.id]) {
          nodePerspectives[nt.id] = [];
        }
        nodePerspectives[nt.id].push(perspectiveId);
      }

      for (const lt of linkTags) {
        if (!linkPerspectives[lt.id]) {
          linkPerspectives[lt.id] = [];
        }
        linkPerspectives[lt.id].push(perspectiveId);
      }
    }

    return {
      nodePerspectives,
      linkPerspectives
    };
  }

  highlightNodes() {
    document.body.classList.add("show-tag-selected");
    if (this.getPersistentPerspectiveCount()) {
      document.body.classList.add("persisted");
    }
    let nodeTags;
    let linkTags;
    let elements;
    let allNodeTags;
    let allLinkTags;
    const activePerspectiveCount = this.getActivePerspectiveCount();
    elements = this.getPerspectivesPerElement();
    allNodeTags = Object.keys(elements.nodePerspectives).map(id => this.canvas.nodeTags[id]);
    allLinkTags = Object.keys(elements.linkPerspectives).map(id => this.canvas.linkTags[id]);
    let connectedLinkIds = {};
    if (allNodeTags) {
      for (const nt of allNodeTags) {
        if (nt) {
          nt.refreshPerspectiveHighlight();
        }
        const links = nt.instance.links();
        if (links && links.length) {
          for (const link of links) {
            connectedLinkIds[link.id] = 1;
          }
        }
      }
    }
    const connectedLinkTags = Object.keys(connectedLinkIds).map(id => this.canvas.linkTags[id]);
    if (connectedLinkTags) {
      for (const lt of connectedLinkTags) {
        lt.refreshPerspectiveHighlight();
      }
    }
    if (allLinkTags) {
      for (const lt of allLinkTags) {
        lt.refreshPerspectiveHighlight();
      }
    }
  }

  unHighlightNodes() {
    document.body.classList.remove('show-tag-selected');
    if (!this.getPersistentPerspectiveCount()) {
      document.body.classList.remove("persisted");
    }
    for (const id in this.canvas.nodeTags) {
      if (this.canvas.nodeTags[id].perspectiveHighlighted) {
        this.canvas.nodeTags[id].perspectiveUnHighlight();
      }
    }

    for (const id in this.canvas.linkTags) {
      if (this.canvas.linkTags[id].perspectiveHighlighted) {
        this.canvas.linkTags[id].perspectiveUnHighlight();
      }
    }
  }

  getPerspectiveNodeTags(perspective) {
    if (!perspective) return [];
    const membership = perspective.getMembers();
    const nodeTags = [];
    for (const uuid in membership.nodes) {
      const nodeTag = this.canvas.nodeTags[uuid];
      if (nodeTag) {
        nodeTags.push(nodeTag);
      }
    }
    return nodeTags;
  }

  getPerspectiveLinkTags(perspective) {
    if (!perspective) return [];
    const membership = perspective.getMembers();
    const linkTags = [];
    for (const uuid in membership.links) {
      const linkTag = this.canvas.linkTags[uuid];
      if (linkTag) {
        linkTags.push(linkTag);
      }
    }
    return linkTags;
  }

  getPerspectives() {
    return store.getPerspectives();
  }

  addNodeToPerspective(perspective, node, batchMessages) {
    perspective.addMemberNode(node);
    const nodeTag = this.canvas.nodeTags[node.id];
    if (nodeTag) {
      nodeTag.updatePerspectiveDots();
      nodeTag.topLevelParentTag().rediscoverLinkPositions();
      if (this.perspectiveIsActive(perspective.id)) {
        nodeTag.perspectiveHighlight();
      }
    }

    if (batchMessages) {
      batchMessages.push(
        this.operation.messages.addToPerspectiveView({
          perspectiveUUID: perspective.id,
          nodeIds: [node.id],
        })
      );
    } else {
      this.operation.addToPerspectiveView({
        perspectiveUUID: perspective.id,
        nodeIds: [node.id],
        onFailure: (err) => {
          console.log(`addToPerspectiveView operation failure: ${err}`);
        }
      });
    }
  }

  addNodeToActivePerspectives(nodeTag, batchMessages) {
    const perspectives = this.getActivePerspectives();
    if (!perspectives || !perspectives.length) {
      return;
    }
    if (!batchMessages) {
      batchMessages = [];
    }
    for (const p of perspectives) {
      this.addNodeToPerspective(p, nodeTag.instance, batchMessages);
    }
    return batchMessages;
  }

  removeNodeFromPerspective(perspective, nodeTag) {
    perspective.removeMemberNode(nodeTag.id);
    nodeTag.updatePerspectiveDots();
    nodeTag.topLevelParentTag().rediscoverLinkPositions();
    if (this.perspectiveIsActive(perspective.id)) {
      nodeTag.refreshPerspectiveHighlight();
    }

    this.operation.deleteFromPerspectiveView({
      perspectiveUUID: perspective.id,
      nodeIds: [nodeTag.id],
      onFailure: (err) => {
        console.log(`deleteFromPerspectiveView operation failure: ${err}`);
      }
    });
  }


  addLinkToPerspective(perspective, link, batchMessages) {
    perspective.addMemberLink(link);
    if (this.perspectiveIsActive(perspective.id)) {
      const linkTag = this.canvas.linkTags[link.id];
      if (linkTag) {
        linkTag.perspectiveHighlight();
      }
    }
    if (batchMessages) {
      batchMessages.push(
        this.operation.messages.addToPerspectiveView({
          perspectiveUUID: perspective.id,
          linkIds: [link.id],
        })
      );
    } else {
      this.operation.addToPerspectiveView({
        perspectiveUUID: perspective.id,
        linkIds: [link.id],
        onFailure: (err) => {
          console.log(`addLinkToPerspective operation failure: ${err}`);
        }
      });
    }
  }

  addLinkToActivePerspectives(link, batchMessages) {
    const perspectives = this.getActivePerspectives();
    if (!perspectives || !perspectives.length) {
      return;
    }
    if (!batchMessages) {
      batchMessages = [];
    }
    for (const p of perspectives) {
      this.addLinkToPerspective(p, link, batchMessages);
    }
    return batchMessages;
  }

  removeLinkFromPerspective(perspective, linkTag) {
    perspective.removeMemberLink(linkTag.id);
    if (this.perspectiveIsActive(perspective.id)) {
      linkTag.refreshPerspectiveHighlight();
    }

    this.operation.deleteFromPerspectiveView({
      perspectiveUUID: perspective.id,
      linkIds: [linkTag.id],
      onFailure: (err) => {
        console.log(`removeLinkFromPerspective operation failure: ${err}`);
      }
    });
  }

  addBatchToPerspective({ perspective, nodeTags, linkTags, nodeViews, operationMessages }) {
    const missingNodeIds = [];
    const missingLinkIds = [];

    if (nodeTags && nodeTags.length) {
      for (const nodeTag of nodeTags) {
        if (perspective.pointNodeUUID && perspective.pointNodeUUID == nodeTags.id) {
          // don't add point node to its own P
          continue;
        }
        if (!perspective.hasMemberNode(nodeTag.id)) {
          missingNodeIds.push(nodeTag.id);
        }

        perspective.addMemberNode(nodeTag.instance);

        if (nodeViews && nodeViews.nodes && nodeViews.nodes[nodeTag.instance.id]) {
          perspective.setAnnotationTitle(nodeTag.instance.id, nodeViews.nodes[nodeTag.instance.id].title);
          perspective.setNodeAnnotationDisplayProperties({
            nodeId: nodeTag.instance.id,
            displayProperties: nodeViews.nodes[nodeTag.instance.id].displayProperties
          });
        }
        if (this.perspectiveIsActive(perspective.id)) {
          nodeTag.perspectiveHighlight();
        }
        nodeTag.updatePerspectiveDots();
      }
    }
    if (linkTags && linkTags.length) {
      for (const linkTag of linkTags) {
        if (!perspective.hasMemberLink(linkTag.id)) {
          missingLinkIds.push(linkTag.id);
        }

        perspective.addMemberLink(linkTag.instance);
        if (this.perspectiveIsActive(perspective.id)) {
          linkTag.perspectiveHighlight();
        }
      }
    }
    nui.bulkRealignLinks(nodeTags);
    if (operationMessages) {
      const message = this.operation.messages.addToPerspectiveView({
        perspectiveUUID: perspective.id,
        nodeIds: missingNodeIds,
        linkIds: missingLinkIds,
        overrides: nodeViews
      });
      operationMessages.push(message);
    } else {
      this.operation.addToPerspectiveView({
        perspectiveUUID: perspective.id,
        nodeIds: missingNodeIds,
        linkIds: missingLinkIds,
        overrides: nodeViews,
        onFailure: (err) => {
          console.log(`addToPerspectiveView operation failure: ${err}`);
        }
      });
    }
  }


  removeBatchFromPerspective({ perspective, nodeTags, linkTags }) {
    if (nodeTags && nodeTags.length) {
      for (const nodeTag of nodeTags) {
        if (perspective.pointNodeUUID && perspective.pointNodeUUID == nodeTags.id) {
          // don't remove point node from its own P
          continue;
        }
        perspective.removeMemberNode(nodeTag.id);
        if (this.perspectiveIsActive(perspective.id)) {
          nodeTag.refreshPerspectiveHighlight();
        }
        nodeTag.updatePerspectiveDots();
      }
    }
    if (linkTags && linkTags.length) {
      for (const linkTag of linkTags) {
        perspective.removeMemberLink(linkTag.id);
        if (this.perspectiveIsActive(perspective.id)) {
          linkTag.refreshPerspectiveHighlight();
        }
      }
    }
    nui.bulkRealignLinks(nodeTags);
    this.operation.deleteFromPerspectiveView({
      perspectiveUUID: perspective.id,
      nodeIds: nodeTags && nodeTags.length ? nodeTags.map(n => n.id) : null,
      linkIds: linkTags && linkTags.length ? linkTags.map(l => l.id) : null,
      onFailure: (err) => {
        console.log(`deleteFromPerspectiveView operation failure: ${err}`);
      }
    });
  }

  // when we delete a card from the map, remove it from all perspective member lists
  removeFromAllPerspectives({nodeIds,linkIds}) {

    store.deleteFromAllPerspectives({
      nodeIds,
      linkIds
    });
    const perspectives = store.getPerspectives();
    const messages = [];
    for (const p of perspectives) {
      const message = this.operation.messages.deleteFromPerspectiveView({
        perspectiveUUID: p.id,
        nodeIds,
        linkIds
      });
      messages.push(message);
    }
    return messages;
  }

  getNodePerspectives(node) {
    if (!node) {
      return [];
    }
    const perspectives = store.getPerspectives();
    return perspectives.filter(p => p.hasMemberNode(node.id));
  }

  editNodeTitle(nodeTag, newTitle, prevTitle, perspective) {
    const instance = nodeTag.instance;
    if (perspective && perspective.hasMemberNode(instance.id)) {

      perspective.setAnnotationTitle(nodeTag.instance.id, newTitle);

      this.operation.updatePerspectiveNodeViews({
        nodeViews: [{
          perspectiveUUID: perspective.id,
          mapNodeUUID: instance.id,
          title: newTitle
        }],
        prevNodeViews: [{
          perspectiveUUID: perspective.id,
          mapNodeUUID: instance.id,
          title: prevTitle || ''
        }],
        onFailure: e => console.log(`updatePerspectiveNodeViews failure: ${e}`),
        onSuccess: () => this.updatePerspectiveNodeTags(perspective.id),
      });
    }
  }

  deleteAnnotation(perspective,nodeId) {
    const nodeTag = this.canvas.nodeTags[nodeId];
    if (!nodeTag) {
      return;
    }
    const prevTitle = perspective.getAnnotation();
    this.editNodeTitle(nodeTag, '', prevTitle, perspective);
    nodeTag.updatePerspectiveDots();
    if (nodeTag.getPerspectiveDots() && nodeTag.getPerspectiveDots().getDot(perspective.id) && nodeTag.getPerspectiveDots().getDot(perspective.id).getAnnotationTag()) {
      nodeTag.getPerspectiveDots().getDot(perspective.id).getAnnotationTag().closeAnnotation();
    }
  }

  addStyleRules(perspective) {
    const id = perspective.id;
    const color = perspective.color;
    const oldStyleEl = document.getElementById(`custom-perspective-color-style-element-${id}`);
    if (oldStyleEl) {
      document.head.removeChild(oldStyleEl);
    }

    const style = document.createElement('style');
    style.id = `custom-perspective-color-style-element-${id}`;
    document.head.appendChild(style);
    style.sheet.insertRule(`.perspective-${id}-color { color: ${color} }`);
    style.sheet.insertRule(`.perspective-${id}-background { background: ${color} }`);
    style.sheet.insertRule(`.perspective-${id}-lighter-background { background: ${shade(color, 0.9)} }`);
    style.sheet.insertRule(`.perspective-${id}-light-background { background: ${shade(color, 0.75)} }`);
    style.sheet.insertRule(`.perspective-${id}-light-background:hover { background: ${shade(color, 0.75)} }`);
    style.sheet.insertRule(`body { --perspective-${id}-light: ${shade(color, 0.9)} }`);
    style.sheet.insertRule(`.show-tag-selected[data-active-perspective-id-${id}="1"] .frontage.tag-selected > .backdrop { box-shadow: 0 0 0 1.5px ${shade(color, 0.25)} }`);
    style.sheet.insertRule(`.show-tag-selected[data-active-perspective-id-${id}="1"] .frontage.tag-selected.top-hover > .backdrop { box-shadow: 0 0 0 2px ${color} }`);
    style.sheet.insertRule(`.show-tag-selected[data-active-perspective-id-${id}="1"] .frontage.tag-selected.selected > .backdrop { box-shadow: 0 0 0 3px ${color} !important}`);
  }

  showAnnotation(perspectiveId, nodeId) {
    this.canvas.hidePopups();
    const perspective = store.getPerspective(perspectiveId);
    const nodeTag = canvas.nodeTags[nodeId];
    pui.addNodeToPerspective(perspective, nodeTag);
    const perspectiveDotTag = this.canvas.nodePerspectiveDotTags[`${nodeId}-${perspectiveId}`];
    const perspectiveAnnotation = this.canvas.nodePerspectiveAnnotations[`${nodeId}-${perspectiveId}`];
    perspectiveDotTag.setActive(true);
    perspectiveAnnotation.refreshAnnotation();
    perspectiveAnnotation.refs.title.focus();
  }

  getActivePerspectivesIdentifier() {
    return 'multi';
    const ids = Object.keys(this.activePerspectives);
    return ids.sort().join('-');
  }

  togglePerspectivePoint({nodeTag,color,operationMessages}) {
    const EYEBALL_WIDTH = 28;
    let deletedPerspective = this.getDeletedPerspectiveFromPointNode(nodeTag.id);
    let activePerspective = this.getPerspectiveFromPointNode(nodeTag.id);
    const origRect = nodeTag.refs.container.getBoundingClientRect();

    if (deletedPerspective) {
      this.undeletePerspective(deletedPerspective.id,operationMessages);
    } else if (activePerspective) {
      this.deletePerspective(activePerspective.id,operationMessages);
    } else {
      const perspective = this.addPerspectivePoint({
        label: nodeTag.instance.title,
        color: color,
        pointNodeUUID: nodeTag.id,
        operationMessages
      });
    }

    this.canvas.nodePerspectivesPanelTag.update();
    nodeTag.updatePerspectiveDots();

      // pan the eyeball width so that the menu doesn't end up covering the card
    const newRect = nodeTag.refs.container.getBoundingClientRect();
    const delta = origRect.right - newRect.right;
    $(this.canvas.refs.canvas).panzoom("pan", delta, 0, { relative: true, animate: true });

    this.refreshPerspectiveLists();
    nodeTag.freshenEyeball();
  }

    // refresh all elements that list perspectives
    // i.e. when one is added or deleted
  refreshPerspectiveLists() {

    const { perspectivesListingTag, nodePerspectivesPanelTag } = this.canvas;

    if (!perspectivesListingTag) return;
    perspectivesListingTag.update();

    for (const item of perspectivesListingTag.getListingItems()) {
      item.update();
      item.refreshTitle();
    }

    if (nodePerspectivesPanelTag) nodePerspectivesPanelTag.update();
  }

  startCreationFromClick() {
    const selectedNodes = this.canvas.getSelectedNodes();
    if (selectedNodes.length == 1) {
      return this.startCreation(selectedNodes[0]);
    }
  }

  startCreation(node) {
    const fromNode = node;
    const { scale, tx, ty } = this.canvas.getMatrix();

    document.body.classList.add('link-dragging');
    if (document.activeElement) document.activeElement.blur();
    this.canvas.setLinkDragging(fromNode.id);

    let sourceNodeTag;
    sourceNodeTag = this.canvas.nodeTags[fromNode.id];
    sourceNodeTag.refs.container.classList.add('link-dragging-no-highlight');
    this.canvas.nodeTools.hide();

    this.canvas.refs.perspectiveDragElement.classList.add('active');
    const selectedPerspective = this.getPerspectiveFromPointNode(fromNode.id);
    let deletedPerspective = this.getDeletedPerspectiveFromPointNode(fromNode.id);

    sourceNodeTag.refs.container.classList.add('no-fade');
    let perspectiveColor;

    sourceNodeTag.enablePerspectiveEyeballTags = true;
    sourceNodeTag.update({forceUpdate: true});
    if (deletedPerspective) {
      perspectiveColor = deletedPerspective.color;
      sourceNodeTag.showPreviewEyeball(this.perspectiveColor);
    } else if (selectedPerspective) {
      perspectiveColor = selectedPerspective.color;
    } else {
      perspectiveColor = pcui.findNextUnusedColor();
      sourceNodeTag.showPreviewEyeball(perspectiveColor);
    }

    const eyeballRect = sourceNodeTag.getEyeballBoundingBox();

    const dragStartPos = {
      x: (eyeballRect.left + eyeballRect.right) / 2,
      y: (eyeballRect.top + eyeballRect.bottom) / 2 + 5
    };

    this.canvas.refs.perspectiveDragElement.style.backgroundColor = perspectiveColor;
    this.canvas.refs.perspectiveDragPath.style.stroke = perspectiveColor;

    const path = this.canvas.refs.perspectiveDragPath;
    path.setAttribute("d", `M 5 5 l 0 0`);

    const moveHandler = e => {
      const el = this.canvas.refs.perspectiveDragElement;
      el.style.left = `${(e.clientX - tx) / scale}px`;
      el.style.top = `${(e.clientY - ty - 10) / scale}px`;

      const dx = (dragStartPos.x - e.clientX) / scale;
      const dy = (dragStartPos.y - e.clientY) / scale;

      const path = this.canvas.refs.perspectiveDragPath;
      path.setAttribute("d", `M 5 5 l ${dx} ${dy}`);
    };
    document.body.addEventListener('mousemove', moveHandler);

    const keyHandler = e => {
      e.stopPropagation();
      const { ESCAPE, DELETE, BACKSPACE } = KEY_CODES;
      if ([ESCAPE, DELETE, BACKSPACE].includes(e.keyCode)) {
        document.body.removeEventListener('click', clickHandler );
        document.body.removeEventListener('mousemove', moveHandler);
        document.body.removeEventListener('keyup', keyHandler);
        document.body.classList.remove('link-dragging');
        sourceNodeTag.refs.container.classList.remove('link-dragging-no-highlight');
        if (!selectedPerspective) {
          sourceNodeTag.hidePreviewEyeball();
        }
        this.canvas.setLinkDragging(false);
        this.canvas.refs.perspectiveDragElement.classList.remove('active');
      }
    };

    const clickHandler = e => {
      document.body.removeEventListener('click', clickHandler );
      document.body.removeEventListener('mousemove', moveHandler);
      document.body.removeEventListener('keyup', keyHandler);
      sourceNodeTag.refs.container.classList.remove('link-dragging-no-highlight', 'no-fade');

      const targetNodeTag = this.canvas.mouseEnteredNode;
      if (targetNodeTag && targetNodeTag !== sourceNodeTag) {
        targetNodeTag.enablePerspectiveDotTags = true;
        targetNodeTag.update({forceUpdate: true});

        const targetNode = targetNodeTag.instance;

        const lastNodeId = this.canvas.linkDragging;
        const lastNode = this.canvas.store.getNode(lastNodeId);

        this.canvas.nodeTags[targetNode.id].discoverPosition();
        this.canvas.nodeTags[lastNodeId].discoverPosition();

        const operationMessages = [];

        let activePerspective = pui.getPerspectiveFromPointNode(lastNodeId);

        if (!activePerspective) {
          pui.togglePerspectivePoint({
            nodeTag: this.canvas.nodeTags[lastNodeId],
            color: perspectiveColor,
            operationMessages,
          });
        }
        activePerspective = pui.getPerspectiveFromPointNode(lastNodeId);

        pui.addNodeToPerspective(activePerspective, targetNode, operationMessages);
        sourceNodeTag.setPointPerspective(activePerspective);
        sourceNodeTag.unsetPreviewEyeball();
        const targetDots = this.canvas.nodePerspectiveDotsTag[targetNodeTag.id];
        const newDot = targetDots.getDot(activePerspective.id);
        if (newDot) {
          newDot.openAnnotation();
        }
        if (pui.hasPersistentPerspective() && !pui.perspectiveIsPersistent(activePerspective.id)) {
          // if we created a new perspective, turn off current active perspective
          pui.unsetAllPerspectives();
        }
        this.canvas.operation.batchOperation({
          messages: operationMessages,
          onFailure: err => console.log(`perspective drag-drop failure: ${err}`),
        });

        this.canvas.nodeTools.summon({
          node: this.canvas.nodeTags[targetNode.id],
          positionReferenceElement: this.canvas.nodeTags[targetNode.id].refs.container,
        });
      } else {
        // no perspective made
        // if aborted, remove the eyeball
        if (!selectedPerspective) {
          sourceNodeTag.hidePreviewEyeball();
        }
      }
      this.canvas.refs.perspectiveDragElement.classList.remove('active');
      document.body.classList.remove('link-dragging');
      this.canvas.setLinkDragging(false);
    };


    document.body.addEventListener('click', clickHandler);
    document.body.addEventListener('keyup', keyHandler);


    return false;
  }

}


// singleton instance
const pui = new PerspectivesUI();
window.pui = pui;
export { PerspectivesUI, pui };



// WEBPACK FOOTER //
// ./starling/src/lib/perspectives-ui.js